import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { findNodeHandle, Platform, TextInput, View } from 'react-native';
import * as ReactNative from 'react-native';
import { isTelevisionRuntime } from '../utils/platformCapabilities';

export type DirectionalFocusProps<T> = {
    ref: React.Ref<T>;
    showSoftInputOnFocus?: boolean;
    nextFocusUp?: number;
    nextFocusDown?: number;
    nextFocusLeft?: number;
    nextFocusRight?: number;
};

export type LoginFocusGraph = {
    server: DirectionalFocusProps<TextInput>;
    loginTab: DirectionalFocusProps<View>;
    signupTab: DirectionalFocusProps<View>;
    username: DirectionalFocusProps<TextInput>;
    password: DirectionalFocusProps<TextInput>;
    confirm: DirectionalFocusProps<TextInput>;
    submit: DirectionalFocusProps<View>;
    quickConnect: DirectionalFocusProps<View>;
    discord: DirectionalFocusProps<View>;
    quickBack: DirectionalFocusProps<View>;
    focusPassword: () => void;
    focusUsername: () => void;
    focusConfirm: () => void;
    activateInput: (key: InputFocusKey) => void;
    setFocused: (key: FocusKey) => void;
};

type FocusKey = 'loginTab' | 'signupTab' | 'server' | 'username' | 'password' | 'confirm' | 'submit' | 'quickConnect' | 'discord' | 'quickBack';
type InputFocusKey = Extract<FocusKey, 'server' | 'username' | 'password' | 'confirm'>;
type LoginMode = 'login' | 'signup' | 'quick' | 'discord';
type TVActions = Partial<Record<Exclude<FocusKey, InputFocusKey>, () => void>>;
type NativeFocusTarget = TextInput | View;
type TVRemoteEvent = { eventType?: string; eventKeyAction?: number; tag?: number };

const useTVEventHandler = (ReactNative as typeof ReactNative & {
    useTVEventHandler?: (handler: (event: TVRemoteEvent) => void) => void;
}).useTVEventHandler ?? (() => undefined);

export function useLoginFocusGraph(mode: LoginMode, tvActions: TVActions, publicMode = false): LoginFocusGraph {
    const signupMode = mode === 'signup';
    const activeRef = useRef<FocusKey>(publicMode ? 'server' : 'loginTab');
    const initialPublicFocusApplied = useRef(false);
    const tvActionsRef = useRef(tvActions);
    const [editingInput, setEditingInput] = useState<InputFocusKey | null>(null);
    const [handles, setHandles] = useState<Partial<Record<FocusKey, number>>>({});
    const loginTabRef = useRef<View | null>(null);
    const signupTabRef = useRef<View | null>(null);
    const serverRef = useRef<TextInput | null>(null);
    const usernameRef = useRef<TextInput | null>(null);
    const passwordRef = useRef<TextInput | null>(null);
    const confirmRef = useRef<TextInput | null>(null);
    const submitRef = useRef<View | null>(null);
    const quickConnectRef = useRef<View | null>(null);
    const discordRef = useRef<View | null>(null);
    const quickBackRef = useRef<View | null>(null);
    tvActionsRef.current = tvActions;

    useEffect(() => {
        if (mode === 'quick' || mode === 'discord') activeRef.current = 'quickBack';
    }, [mode]);

    const register = useCallback((key: FocusKey, targetRef: React.MutableRefObject<NativeFocusTarget | null>) => (
        target: NativeFocusTarget | null,
    ) => {
        targetRef.current = target;
        // Web navigation uses the shared semantic focus graph. Numeric native
        // handles are only needed by the TV focus engine, and calling
        // findNodeHandle on React Native Web invokes deprecated findDOMNode.
        const handle = Platform.OS === 'web' || !target ? null : findNodeHandle(target);
        if (handle) setHandles((current) => current[key] === handle ? current : { ...current, [key]: handle });
    }, []);

    const focusRefs = useMemo(() => ({
        loginTab: register('loginTab', loginTabRef) as React.Ref<View>,
        signupTab: register('signupTab', signupTabRef) as React.Ref<View>,
        server: register('server', serverRef) as React.Ref<TextInput>,
        username: register('username', usernameRef) as React.Ref<TextInput>,
        password: register('password', passwordRef) as React.Ref<TextInput>,
        confirm: register('confirm', confirmRef) as React.Ref<TextInput>,
        submit: register('submit', submitRef) as React.Ref<View>,
        quickConnect: register('quickConnect', quickConnectRef) as React.Ref<View>,
        discord: register('discord', discordRef) as React.Ref<View>,
        quickBack: register('quickBack', quickBackRef) as React.Ref<View>,
    }), [register]);

    const targets: Record<FocusKey, React.MutableRefObject<NativeFocusTarget | null>> = {
        loginTab: loginTabRef,
        signupTab: signupTabRef,
        server: serverRef,
        username: usernameRef,
        password: passwordRef,
        confirm: confirmRef,
        submit: submitRef,
        quickConnect: quickConnectRef,
        discord: discordRef,
        quickBack: quickBackRef,
    };

    const focusTarget = useCallback((key: FocusKey) => {
        const target = targets[key].current as NativeFocusTarget & { requestTVFocus?: () => void; focus?: () => void };
        if (!target) return;
        setEditingInput(null);
        activeRef.current = key;
        if (target.requestTVFocus) target.requestTVFocus();
        else target.focus?.();
    }, []);

    useEffect(() => {
        if (!publicMode || !isTelevisionRuntime() || mode === 'quick' || mode === 'discord' || initialPublicFocusApplied.current || !handles.server) return;
        initialPublicFocusApplied.current = true;
        focusTarget('server');
    }, [focusTarget, handles.server, mode, publicMode]);

    const activateInput = useCallback((key: InputFocusKey) => {
        if (!isTelevisionRuntime()) return;
        const target = targets[key].current as TextInput | null;
        if (!target) return;
        setEditingInput(key);
        target.blur();
        requestAnimationFrame(() => target.focus());
    }, []);

    useTVEventHandler(useCallback((event) => {
        // The login route owns its complete graph because TextInput controls
        // are not registered in the shared semantic resolver. Handle one
        // native phase only so a remote press advances once.
        if (event.eventKeyAction === 0) return;
        const active = activeRef.current;
        const eventTarget = (Object.entries(handles) as [FocusKey, number][])
            .find(([, handle]) => handle === event.tag)?.[0];
        const selected = eventTarget ?? active;
        if (event.eventType === 'select' && ['server', 'username', 'password', 'confirm'].includes(selected)) {
            activateInput(selected as InputFocusKey);
            return;
        }
        if (event.eventType === 'select') {
            tvActionsRef.current[selected as Exclude<FocusKey, InputFocusKey>]?.();
            return;
        }
        if (!['up', 'down', 'left', 'right'].includes(event.eventType ?? '')) return;
        const activeTab: FocusKey = signupMode ? 'signupTab' : 'loginTab';
        const previousFromSubmit: FocusKey = signupMode ? 'confirm' : 'password';
        let next: FocusKey | undefined;

        if (event.eventType === 'left' || event.eventType === 'right') {
            if (active === 'loginTab' && event.eventType === 'right') next = 'signupTab';
            else if (active === 'signupTab' && event.eventType === 'left') next = 'loginTab';
        } else if (event.eventType === 'down') {
            const down: Partial<Record<FocusKey, FocusKey>> = {
                loginTab: 'username', signupTab: 'username', server: 'username', username: 'password',
                password: signupMode ? 'confirm' : 'submit', confirm: 'submit',
                submit: 'quickConnect', quickConnect: 'discord', discord: publicMode ? undefined : activeTab,
            };
            next = down[active];
        } else {
            const up: Partial<Record<FocusKey, FocusKey>> = {
                loginTab: 'discord', signupTab: 'discord', username: activeTab,
                password: 'username', confirm: 'password', submit: previousFromSubmit,
                quickConnect: 'submit', discord: 'quickConnect', server: publicMode ? undefined : 'discord',
            };
            next = publicMode && active === 'username' ? 'server' : up[active];
        }
        if (!next) return;
        if (event.tag === handles[next]) {
            activeRef.current = next;
            setEditingInput(null);
        } else {
            focusTarget(next);
        }
    }, [activateInput, focusTarget, handles, publicMode, signupMode]));

    return useMemo(() => ({
            // The private build does not render the server URL field. Point the
            // native TV focus engine at the first visible input in that mode;
            // leaving this as `handles.server` makes DOWN from the mode tabs a
            // dead end because there is no native view to receive focus.
            loginTab: { ref: focusRefs.loginTab, nextFocusRight: handles.signupTab, nextFocusDown: publicMode ? handles.server : handles.username },
            signupTab: { ref: focusRefs.signupTab, nextFocusLeft: handles.loginTab, nextFocusDown: publicMode ? handles.server : handles.username },
            server: { ref: focusRefs.server, showSoftInputOnFocus: !isTelevisionRuntime() || editingInput === 'server', nextFocusUp: publicMode ? undefined : handles.loginTab, nextFocusDown: handles.username },
            username: { ref: focusRefs.username, showSoftInputOnFocus: !isTelevisionRuntime() || editingInput === 'username', nextFocusUp: handles.server, nextFocusDown: handles.password },
            password: { ref: focusRefs.password, showSoftInputOnFocus: !isTelevisionRuntime() || editingInput === 'password', nextFocusUp: handles.username, nextFocusDown: signupMode ? handles.confirm : handles.submit },
            confirm: { ref: focusRefs.confirm, showSoftInputOnFocus: !isTelevisionRuntime() || editingInput === 'confirm', nextFocusUp: handles.password, nextFocusDown: handles.submit },
            submit: { ref: focusRefs.submit, nextFocusUp: signupMode ? handles.confirm : handles.password, nextFocusDown: handles.quickConnect },
            quickConnect: { ref: focusRefs.quickConnect, nextFocusUp: handles.submit, nextFocusDown: handles.discord },
            discord: { ref: focusRefs.discord, nextFocusUp: handles.quickConnect, nextFocusDown: publicMode ? handles.server : handles.loginTab },
            quickBack: { ref: focusRefs.quickBack },
            focusPassword: () => passwordRef.current?.focus(),
            focusUsername: () => usernameRef.current?.focus(),
            focusConfirm: () => confirmRef.current?.focus(),
            activateInput,
            setFocused: (key: FocusKey) => { activeRef.current = key; },
    }), [activateInput, editingInput, focusRefs, handles, publicMode]);
}
